import apiCall from "../AuthorizedApi";

export interface Category {
  id: number;
  name: string;
  slug: string;
  is_active: boolean;
  created_at: string;
}

export interface EquipmentUnit {
  id: number;
  serial_number: string | null;
  status: "available" | "assigned";
  assignment: string | null;
}

export interface EquipmentItem {
  id: number;
  name: string;
  category: number;
  category_name?: string;
  brand: string;
  model: string;
  serial_number: string;
  quantity?: number;
  total_quantity?: number;
  total_assigned?: number;
  remaining_total?: number;
  assigned_serial_numbers?: string[];
  available_serial_numbers?: string[];
  items?: EquipmentUnit[];
  ownership_type: "studio_owned" | "rent" | "sale";
  status: "available" | "assigned" | "under_service" | "sold" | "retired";
  purchase_date?: string;
  purchase_price?: string;
  purchase_vendor?: string;
  warranty_start_date?: string;
  warranty_expiry_date?: string;
  insurance_renewal_date?: string;
  last_service_date?: string;
  next_service_date?: string;
  service_notes?: string;
  notes?: string;
  attachments?: any[];
  service_logs?: any[];
  active_assignment?: any;
  branch_id?: number | null;
  branch_name?: string | null;
  created_at: string;
  updated_at: string;
}

export interface EquipmentCreateUpdatePayload {
  branch_id?: number | null;
  name?: string;
  category?: number;
  brand?: string;
  model?: string;
  serial_number?: string;
  quantity?: number;
  items?: { serial_number?: string }[];
  ownership_type?: "studio_owned" | "rent" | "sale";
  status?: string;
  purchase_date?: string | null;
  purchase_price?: string;
  purchase_vendor?: string;
  warranty_start_date?: string | null;
  warranty_expiry_date?: string | null;
  next_service_date?: string | null;
  service_notes?: string;
}

export interface AssignEquipmentPayload {
  team_member_id: number;
  serial_numbers?: string[];
  unit_ids?: number[];
  quantity?: number;
  expected_return_at?: string;
  remarks?: string;
}

export interface ReturnEquipmentPayload {
  serial_numbers?: string[];
  unit_ids?: number[];
  assignment_id?: string;
  remarks?: string;
}

export interface DashboardStats {
  total_equipment: number;
  equipment_assigned: number;
  equipment_under_service: number;
  equipment_available: number;
  warranty_expiring_soon: number;
  service_due_this_month: number;
  available_for_rent: number;
  available_for_sale: number;
  overdue_returns: number;
}

export const getEquipmentDashboard = async (params?: { branch_id?: number | string }): Promise<DashboardStats> => {
  return await apiCall({
    method: "GET",
    url: "/equipment/dashboard/",
    params,
  });
};

export const getCategories = async (params?: {
  search?: string;
  active_only?: boolean;
  page?: number;
  page_size?: number;
  ordering?: string;
}): Promise<{ count: number; results: Category[] }> => {
  return await apiCall({
    method: "GET",
    url: "/equipment/categories/",
    params,
  });
};

export const createCategory = async (data: { name: string; is_active?: boolean }): Promise<Category> => {
  return await apiCall({
    method: "POST",
    url: "/equipment/categories/",
    data,
  });
};

export const updateCategory = async (id: number, data: Partial<Category>): Promise<Category> => {
  return await apiCall({
    method: "PATCH",
    url: `/equipment/categories/${id}/`,
    data,
  });
};

export const deleteCategory = async (id: number): Promise<void> => {
  return await apiCall({
    method: "DELETE",
    url: `/equipment/categories/${id}/`,
  });
};

export const getEquipmentItems = async (params?: {
  branch_id?: number | string;
  search?: string;
  status?: string;
  ownership_type?: string;
  category_id?: number | string;
  page?: number;
  page_size?: number;
  ordering?: string;
}): Promise<{ count: number; results: EquipmentItem[] }> => {
  return await apiCall({
    method: "GET",
    url: "/equipment/items/",
    params,
  });
};

export const getEquipmentItemDetail = async (id: number | string): Promise<EquipmentItem> => {
  return await apiCall({
    method: "GET",
    url: `/equipment/items/${id}/`,
  });
};

export const createEquipmentItem = async (
  data: EquipmentCreateUpdatePayload
): Promise<EquipmentItem> => {
  return await apiCall({
    method: "POST",
    url: "/equipment/items/",
    data,
  });
};

export const updateEquipmentItem = async (
  id: number | string,
  data: EquipmentCreateUpdatePayload
): Promise<EquipmentItem> => {
  return await apiCall({
    method: "PATCH",
    url: `/equipment/items/${id}/`,
    data,
  });
};

export const deleteEquipmentItem = async (id: number | string): Promise<void> => {
  return await apiCall({
    method: "DELETE",
    url: `/equipment/items/${id}/`,
  });
};

export const assignEquipment = async (
  id: number | string,
  data: AssignEquipmentPayload
): Promise<any> => {
  return await apiCall({
    method: "POST",
    url: `/equipment/items/${id}/assign/`,
    data,
  });
};

export const returnEquipment = async (
  id: number | string,
  data: ReturnEquipmentPayload
): Promise<any> => {
  return await apiCall({
    method: "POST",
    url: `/equipment/items/${id}/return/`,
    data,
  });
};

export const getEquipmentAssignments = async (
  id: number | string,
  params?: { page?: number; page_size?: number }
): Promise<any> => {
  return await apiCall({
    method: "GET",
    url: `/equipment/items/${id}/assignments/`,
    params,
  });
};

export const getGlobalAssignments = async (params?: {
  branch_id?: number | string;
  return_status?: string;
  equipment_id?: number | string;
  team_member_id?: number | string;
  page?: number;
  page_size?: number;
}): Promise<any> => {
  return await apiCall({
    method: "GET",
    url: "/equipment/assignments/",
    params,
  });
};

export const addEquipmentAttachment = async (
  id: number | string,
  data: { attachment_type?: string; file_name: string; file_url: string; notes?: string }
): Promise<any> => {
  return await apiCall({
    method: "POST",
    url: `/equipment/items/${id}/attachments/`,
    data,
  });
};

export const createServiceLog = async (
  id: number | string,
  data: { service_date: string; vendor?: string; cost?: string; notes?: string; next_service_date?: string }
): Promise<any> => {
  return await apiCall({
    method: "POST",
    url: `/equipment/items/${id}/service-logs/`,
    data,
  });
};

export const getEquipmentAuditLog = async (id: number | string): Promise<any> => {
  return await apiCall({
    method: "GET",
    url: `/equipment/items/${id}/audit-log/`,
  });
};

export const getReminders = async (params?: {
  branch_id?: number | string;
  status?: string;
  reminder_type?: string;
  page?: number;
  page_size?: number;
  ordering?: string;
}): Promise<any> => {
  return await apiCall({
    method: "GET",
    url: "/equipment/reminders/",
    params,
  });
};

export const dismissReminder = async (id: string): Promise<any> => {
  return await apiCall({
    method: "POST",
    url: `/equipment/reminders/${id}/dismiss/`,
  });
};

export const processDueReminders = async (): Promise<any> => {
  return await apiCall({
    method: "POST",
    url: "/equipment/reminders/process-due/",
  });
};

export const syncReminders = async (id: number | string): Promise<any> => {
  return await apiCall({
    method: "POST",
    url: `/equipment/items/${id}/sync-reminders/`,
  });
};
